import { FontAwesome5, FontAwesome6, MaterialCommunityIcons } from '@expo/vector-icons';
import { FirebaseAuthTypes, signOut as signOutMobile } from '@react-native-firebase/auth';
import { router, useLocalSearchParams, useNavigation } from 'expo-router';
import { Auth, signOut as signOutWeb } from 'firebase/auth';
import { capitalize } from 'lodash';
import { useEffect, useLayoutEffect, useState } from 'react';
import { Linking, Platform, StyleSheet, View } from 'react-native';
import { Checkbox } from 'react-native-paper';
import { SafeAreaView } from 'react-native-safe-area-context';

import { useDonateToNpo } from '@/api/hooks/donationHooks';
import BrandedButton from '@/components/buttons/BrandedButton';
import GoBackButton from '@/components/buttons/GoBackButton';
import TokneizedACHView from '@/components/paymentMethods/TokenizedACHView';
import TokenizedCardView from '@/components/paymentMethods/TokenizedCardView';
import Text from '@/components/Text';
import { APPSFLYER_CONFIG } from '@/constants/config';
import { giftRecurringOptions } from '@/constants/options/DonationOptions';
import { useAuth } from '@/contexts/authContext';
import { useEndFlowModal } from '@/contexts/endFlowModalContext';
import { useMeFavorites } from '@/contexts/favoritesContext';
import { auth } from '@/firebaseConfig';
import { decryptObject } from '@/utils/crypto';
import { formatCurrency } from '@/utils/currencyFormat';
import { formatDateYear } from '@/utils/dateFormats';
import { i18n } from '@/utils/i18n';
import { safeJsonParse } from '@/utils/jsonParse';

import { TokenizedACHData } from '../paymentMethods/achList';
import { TokenizedCreditCardData } from '../paymentMethods/creditCardList';

const DonationPayment = () => {
  const navigation = useNavigation();

  const { payload } = useLocalSearchParams<{
    payload: string;
  }>();
  const transactionFeesLabel = i18n.t('donation.contribute_fee_label');

  const { state, setNpoSubscribed, dispatch, clearCurrentUser } = useAuth();
  const { clearFavorites } = useMeFavorites();

  const { showSuccess, showError } = useEndFlowModal();

  const { mutateAsync: donateToNpo, isPending: isDonateToNpoPending } = useDonateToNpo();

  const [transactionFees, setTransactionFees] = useState(false);

  const [payloadState, setPayloadState] = useState<{
    tokenizedCard?: string;
    tokenizedACH?: string;
    donationData: string;
    fromIOS?: string;
  }>();

  const [parsedDonationData, setParsedDonationData] = useState<{
    individualUserId: string;
    nonProfitOrgId: string;
    giftType: string;
    giftRecurringOption?: string;
    optIn?: boolean;
    amount: string;
    date: string;
    whereToDonate: string;
    notes?: string;
  }>();
  const [parsedTokenizedCard, setParsedTokenizedCard] = useState<TokenizedCreditCardData>();
  const [parsedTokenizedACH, setParsedTokenizedACH] = useState<TokenizedACHData>();

  useEffect(() => {
    const loadPayloadParams = async () => {
      const payloadObject = await decryptObject<{
        tokenizedCard?: string;
        tokenizedACH?: string;
        donationData: string;
        fromIOS?: string;
      }>(decodeURIComponent(payload));

      if (payloadObject) {
        setPayloadState(payloadObject);
        setParsedDonationData(
          safeJsonParse<{
            individualUserId: string;
            nonProfitOrgId: string;
            giftType: string;
            giftRecurringOption?: string;
            optIn?: boolean;
            amount: string;
            date: string;
            whereToDonate: string;
            notes?: string;
          }>(payloadObject.donationData ?? '')
        );
        setParsedTokenizedCard(safeJsonParse<TokenizedCreditCardData>(payloadObject?.tokenizedCard ?? ''));
        setParsedTokenizedACH(safeJsonParse<TokenizedACHData>(payloadObject?.tokenizedACH ?? ''));
      }
    };

    if (payload) {
      loadPayloadParams();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payload]);

  useLayoutEffect(() => {
    if (payloadState?.fromIOS) {
      navigation.setOptions({
        headerLeft: () => (
          <GoBackButton
            onPress={() => {
              dispatch({ type: 'SIGN_OUT' });
              clearCurrentUser();
              if (Platform.OS === 'web') {
                signOutWeb(auth() as Auth);
              } else {
                signOutMobile(auth() as FirebaseAuthTypes.Module);
              }
              clearFavorites();
              const params = new URLSearchParams({
                deep_link_value: 'donation',
                dismissBrowser: 'true'
              });
              const backRoute = `${APPSFLYER_CONFIG.af_dp}?${params.toString()}`;
              Linking.openURL(backRoute);
            }}
          />
        )
      });
    }
  }, [clearCurrentUser, clearFavorites, dispatch, payloadState?.fromIOS, navigation]);

  const handleConfirm = () => {
    if (parsedDonationData)
      try {
        donateToNpo(
          {
            // Inverted optIn (Now is optOut of updates)
            optIn: parsedDonationData.optIn !== undefined ? !parsedDonationData.optIn : true,
            nonProfitOrgId: parsedDonationData.nonProfitOrgId,
            giftType: parsedDonationData.giftType,
            amount: parsedDonationData.amount,
            cardKey: parsedTokenizedCard?.card_key,
            checkKey: parsedTokenizedACH?.check_key,
            surcharge: transactionFees,
            notes: parsedDonationData.notes,
            ...(parsedDonationData.giftType === 'recurring' && {
              contract: {
                bill_amount: parsedDonationData.amount,
                start_date: parsedDonationData.date ?? new Date().toISOString(),
                billing_period: parsedDonationData.giftRecurringOption ?? ''
              }
            })
          },
          {
            onSuccess: (_) => {
              // Inverted optIn (Now is optOut of updates)
              if (parsedDonationData.optIn !== undefined && !parsedDonationData.optIn) {
                setNpoSubscribed({
                  subscribedToDonationChatRooms: {
                    ...state.user?.onBoardingPreference?.subscribedToDonationChatRooms,
                    [parsedDonationData.nonProfitOrgId]: true
                  }
                });
              }

              const oneTimeMessage = i18n.t('donation.success_message', {
                AMOUNT: formatCurrency(+parsedDonationData.amount),
                WHERE: parsedDonationData.whereToDonate
              });

              const selectedOption = giftRecurringOptions?.find(
                (option) => option.value === parsedDonationData.giftRecurringOption
              );

              const subscriptionMessage = i18n.t('donation.subscription_success_message', {
                FREQUENCY: selectedOption?.label,
                AMOUNT: formatCurrency(+parsedDonationData.amount),
                WHERE: parsedDonationData.whereToDonate,
                DATE: formatDateYear(parsedDonationData.date)
              });

              showSuccess({
                title: i18n.t('end_flow.success_title'),
                message:
                  parsedDonationData.giftType === 'recurring' && selectedOption ? subscriptionMessage : oneTimeMessage,
                ...(payloadState?.fromIOS && {
                  onButtonPress: () => {
                    dispatch({ type: 'SIGN_OUT' });
                    clearCurrentUser();
                    if (Platform.OS === 'web') {
                      signOutWeb(auth() as Auth);
                    } else {
                      signOutMobile(auth() as FirebaseAuthTypes.Module);
                    }
                    clearFavorites();
                    const params = new URLSearchParams({
                      deep_link_value: 'donation',
                      dismissBrowser: 'true'
                    });
                    const backRoute = `${APPSFLYER_CONFIG.af_dp}?${params.toString()}`;
                    Linking.openURL(backRoute);
                  }
                })
              });

              if (!payloadState?.fromIOS) {
                router.dismissAll();
                router.navigate('/home/npoTabs');
              }
            }
          }
        );
      } catch (e) {
        console.error(e);
        showError({});
        if (payloadState?.fromIOS) {
          dispatch({ type: 'SIGN_OUT' });
          clearCurrentUser();
          if (Platform.OS === 'web') {
            signOutWeb(auth() as Auth);
          } else {
            signOutMobile(auth() as FirebaseAuthTypes.Module);
          }
          clearFavorites();
          const params = new URLSearchParams({
            deep_link_value: 'donation',
            dismissBrowser: 'true'
          });
          const backRoute = `${APPSFLYER_CONFIG.af_dp}?${params.toString()}`;
          Linking.openURL(backRoute);
        }
      }
  };

  return (
    <SafeAreaView style={styles.flexOne} edges={['bottom']}>
      <View style={styles.container}>
        <Text style={styles.title} fontWeight="700">
          {i18n.t('donation_summary.title')}
        </Text>

        {/* Supporting */}
        <View style={styles.dataRow}>
          <MaterialCommunityIcons name="charity" size={30} color="black" />
          <Text style={styles.data}>
            {i18n.t('donation_summary.supporting', { WHERE: parsedDonationData?.whereToDonate })}
          </Text>
        </View>

        {/* Amount */}
        <View style={styles.dataRow}>
          <FontAwesome5 name="money-bill-wave" size={22} color="black" />
          {parsedDonationData?.amount && (
            <Text style={styles.data}>
              {i18n.t('donation_summary.amount', { AMOUNT: formatCurrency(+parsedDonationData.amount) })}
            </Text>
          )}
        </View>

        {/* Type */}
        <View style={styles.dataRow}>
          {parsedDonationData?.giftType === 'one-time' && (
            <MaterialCommunityIcons name="gift" size={28} color="black" />
          )}
          {parsedDonationData?.giftType === 'recurring' && <FontAwesome5 name="calendar-alt" size={26} color="black" />}
          <Text style={styles.data}>
            {i18n.t('donation_summary.type', { TYPE: capitalize(parsedDonationData?.giftType) })}
          </Text>
        </View>

        {/* Frequency */}
        {parsedDonationData?.giftRecurringOption && (
          <View style={styles.dataRow}>
            <FontAwesome6 name="clock-rotate-left" size={26} color="black" />
            <Text style={styles.data}>
              {i18n.t('donation_summary.frequency', { FREQUENCY: parsedDonationData.giftRecurringOption })}
            </Text>
          </View>
        )}

        {/* Payment Method */}
        <View style={styles.dataRow}>
          <Text style={styles.title} fontWeight="700">
            {i18n.t('donation_summary.payment_method')}
          </Text>
        </View>

        {parsedTokenizedCard && <TokenizedCardView tokenizedCard={parsedTokenizedCard} showDelete={false} />}
        {parsedTokenizedACH && <TokneizedACHView tokenizedAch={parsedTokenizedACH} showDelete={false} />}

        {/* Transaction Fees */}
        <View style={styles.checkContainer}>
          <Checkbox.Android
            status={transactionFees ? 'checked' : 'unchecked'}
            onPress={() => setTransactionFees(!transactionFees)}
            color={'black'}
          />
          <Text style={styles.checkLabel}>{transactionFeesLabel}</Text>
        </View>

        <BrandedButton onPress={handleConfirm} loading={isDonateToNpoPending}>
          {i18n.t('mix.confirm')}
        </BrandedButton>
      </View>
    </SafeAreaView>
  );
};

export default DonationPayment;

const styles = StyleSheet.create({
  flexOne: {
    flex: 1
  },

  container: {
    flex: 1,
    paddingHorizontal: Platform.OS !== 'web' ? 18 : 0,
    paddingVertical: 18,
    gap: 20
  },

  title: {
    fontSize: 24,
    color: 'black'
  },

  dataRow: {
    flexDirection: 'row',
    gap: 10,
    alignItems: 'center'
  },

  data: {
    fontSize: 18,
    color: 'black'
  },

  checkContainer: {
    flexDirection: 'row',
    alignItems: 'center'
  },

  checkLabel: {
    fontSize: 14,
    color: 'black',
    fontWeight: '400'
  }
});
